import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar, Nav, Container, Dropdown, Image, Offcanvas } from 'react-bootstrap';
import LanguageSelector from './LanguageSelector';
import { t } from '../utils/i18n';
import { authUrl } from '../utils/apiUrl';

/**
 * App navbar: horizontal links on desktop, slide-in drawer on mobile.
 */
function AppNavbar({
  user,
  onLogout,
  language,
  isSuperUser,
  aiServiceStatus = false,
  activeSection,
  onNavSelect,
}) {
  const navigate = useNavigate();
  const [navExpanded, setNavExpanded] = useState(false);
  const isBlogAdmin = activeSection === 'blog-admin';

  const closeMenu = () => setNavExpanded(false);

  const handleMainNavClick = (tab) => {
    closeMenu();
    if (activeSection === 'blog-admin') {
      const hash = tab === 'stats' ? 'statistics' : tab;
      navigate(`/app#${hash}`);
    } else if (onNavSelect) {
      onNavSelect(tab);
    }
  };

  const goSettings = () => {
    closeMenu();
    if (onNavSelect) onNavSelect('settings');
    else navigate('/app#settings');
  };

  const goBlogAdmin = () => {
    closeMenu();
    navigate('/app/blog-admin');
  };

  const handleLogout = async () => {
    closeMenu();
    try {
      await fetch(authUrl('/auth/logout'), { method: 'POST', credentials: 'include' });
    } catch (e) {
      /* ignore */
    }
    onLogout?.();
    navigate('/', { replace: true });
  };

  const mainNavLinks = (
    <>
      <Nav.Link
        active={!isBlogAdmin && activeSection === 'recommendations'}
        onClick={() => handleMainNavClick('recommendations')}
      >
        {t('nav.library', language)}
      </Nav.Link>
      <Nav.Link
        active={!isBlogAdmin && activeSection === 'stats'}
        onClick={() => handleMainNavClick('stats')}
      >
        {t('nav.stats', language)}
      </Nav.Link>
    </>
  );

  const userMenuItems = user ? (
    <>
      <Nav.Link
        active={!isBlogAdmin && activeSection === 'settings'}
        onClick={goSettings}
        className="miskosas-nav-drawer__action"
      >
        ⚙️ {t('nav.settings', language)}
      </Nav.Link>
      {isSuperUser && (
        <Nav.Link
          active={isBlogAdmin}
          onClick={goBlogAdmin}
          className="miskosas-nav-drawer__action"
        >
          📝 {t('nav.blogAdmin', language)}
        </Nav.Link>
      )}
      <Nav.Link onClick={handleLogout} className="miskosas-nav-drawer__action miskosas-nav-drawer__logout">
        {t('common.logout', language)}
      </Nav.Link>
    </>
  ) : null;

  const aiStatusBadge = aiServiceStatus ? (
    <span className="miskosas-nav-drawer__ai text-success small" title={t('common.aiAvailable', language)}>
      🟢 {t('common.aiAvailable', language)}
    </span>
  ) : (
    <span className="miskosas-nav-drawer__ai text-warning small" title={t('common.aiNotAvailable', language)}>
      🟡 {t('common.aiNotAvailable', language)}
    </span>
  );

  return (
    <>
      <Navbar className="mb-3 py-2 dashboard-navbar miskosas-navbar navbar-dark" expand="lg">
        <Container fluid className="px-2 px-sm-3 miskosas-navbar__inner">
          <Navbar.Brand
            as="button"
            className="fw-bold border-0 bg-transparent text-white me-lg-3"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              closeMenu();
              navigate(user ? '/app' : '/');
            }}
          >
            MisKosas
          </Navbar.Brand>

          <div className="d-none d-lg-flex align-items-center flex-grow-1 miskosas-navbar__desktop">
            <Nav className="me-auto nav-compact flex-row">{mainNavLinks}</Nav>
            <Nav className="align-items-center flex-nowrap flex-row gap-1 miskosas-navbar__utilities">
              <div className="me-2 me-md-3">
                <LanguageSelector />
              </div>
              {aiServiceStatus ? (
                <span className="text-success small me-2 me-md-3" title={t('common.aiAvailable', language)}>
                  🟢
                </span>
              ) : (
                <span className="text-warning small me-2 me-md-3" title={t('common.aiNotAvailable', language)}>
                  🟡
                </span>
              )}
              {user && (
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="link"
                    id="user-dropdown"
                    className="miskosas-navbar__user-toggle d-flex align-items-center border-0 py-1 px-2 text-white text-decoration-none"
                  >
                    {user.picture ? (
                      <Image src={user.picture} roundedCircle width={28} height={28} className="me-2" alt="" />
                    ) : (
                      <span className="me-2">👤</span>
                    )}
                    <span
                      className="text-nowrap"
                      style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {user.name || user.email}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    <Dropdown.ItemText className="small text-muted py-1">{user.email}</Dropdown.ItemText>
                    <Dropdown.Divider className="my-1" />
                    <Dropdown.Item className="py-2" onClick={goSettings}>
                      ⚙️ {t('nav.settings', language)}
                    </Dropdown.Item>
                    {isSuperUser && (
                      <>
                        <Dropdown.Divider className="my-1" />
                        <Dropdown.Item className="py-2" onClick={goBlogAdmin}>
                          📝 {t('nav.blogAdmin', language)}
                        </Dropdown.Item>
                      </>
                    )}
                    <Dropdown.Divider className="my-1" />
                    <Dropdown.Item className="py-2" onClick={handleLogout}>
                      {t('common.logout', language)}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Nav>
          </div>

          <Navbar.Toggle
            className="d-lg-none ms-auto miskosas-navbar-toggler"
            aria-controls="miskosas-nav-drawer"
            aria-label={t('nav.openMenu', language)}
            onClick={() => setNavExpanded(true)}
          />
        </Container>
      </Navbar>

      <Offcanvas
        show={navExpanded}
        onHide={closeMenu}
        placement="end"
        className="miskosas-nav-drawer"
        id="miskosas-nav-drawer"
      >
        <Offcanvas.Header closeButton closeVariant="white" className="miskosas-nav-drawer__header">
          <Offcanvas.Title className="text-white fw-bold">MisKosas</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="miskosas-nav-drawer__body">
          {user && (
            <div className="miskosas-nav-drawer__profile d-flex align-items-center gap-2 mb-3 pb-3 border-bottom">
              {user.picture ? (
                <Image src={user.picture} roundedCircle width={40} height={40} alt="" />
              ) : (
                <span className="miskosas-nav-drawer__avatar">👤</span>
              )}
              <div className="min-w-0">
                <div className="fw-semibold text-truncate">{user.name || user.email}</div>
                <div className="small text-muted text-truncate">{user.email}</div>
              </div>
            </div>
          )}

          <p className="miskosas-nav-drawer__section-label small text-muted text-uppercase mb-2">
            {t('nav.menuSection', language)}
          </p>
          <Nav className="flex-column miskosas-nav-drawer__nav gap-1">{mainNavLinks}</Nav>

          <hr className="my-3" />

          <p className="miskosas-nav-drawer__section-label small text-muted text-uppercase mb-2">
            {t('nav.menuPreferences', language)}
          </p>
          <div className="mb-3">
            <LanguageSelector className="w-100" />
          </div>
          {aiStatusBadge}

          {user ? (
            <>
              <hr className="my-3" />
              <p className="miskosas-nav-drawer__section-label small text-muted text-uppercase mb-2">
                {t('nav.menuAccount', language)}
              </p>
              <Nav className="flex-column gap-1">{userMenuItems}</Nav>
            </>
          ) : null}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export default AppNavbar;
